import { useEffect, useRef, useState } from "react";
import type { ClubBook } from "@pagemates/shared";
import { Cover } from "./BookBits";
import { ChevronIcon } from "./Icons";
import { shelf } from "@pagemates/shared/copy";

const txt = shelf.bookPicker;

const RECENT = 5;
const fullDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

/**
 * Talk header: the book's cover and title; with more than one book, a menu
 * with covers. Finished books beyond the five most recent sit behind
 * "Show more", grouped by year, so a long history stays a short menu.
 */
export function BookPicker({ books, current, onPick }: { books: ClubBook[]; current: ClubBook; onPick: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const many = books.length > 1;
  const cur = books.find((b) => b.status === "current");
  const finished = books.filter((b) => b.status === "finished"); // already newest first
  const recent = finished.slice(0, RECENT);
  const older = finished.slice(RECENT);
  const byYear = new Map<string, ClubBook[]>();
  for (const b of older) { const y = b.finishedAt ? String(new Date(b.finishedAt).getFullYear()) : txt.earlier; byYear.set(y, [...(byYear.get(y) ?? []), b]); }

  const Item = ({ b }: { b: ClubBook }) => (
    <button type="button" role="option" aria-selected={b.id === current.id} onClick={() => { setOpen(false); onPick(b.id); }}>
      <Cover book={b.book} size="sm" />
      <span className="bp-t"><span>{b.book.title}</span>
        <span className="muted small">{b.status === "current" ? txt.readingNow : b.finishedAt ? txt.finishedOn(fullDate(b.finishedAt)) : txt.finished}</span></span>
    </button>
  );

  return (
    <div className="bookpick" ref={ref}>
      <button type="button" className="bp-current" disabled={!many} aria-haspopup={many ? "listbox" : undefined} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Cover book={current.book} size="sm" />
        <span className="bp-title">{current.book.title}</span>
        {many && <ChevronIcon className="bp-chev" width={16} height={16} />}
      </button>
      {open && (
        <div className="bp-menu" role="listbox" aria-label={txt.whichBook}>
          {cur && <Item b={cur} />}
          {recent.length > 0 && cur && <div className="bp-head">{txt.finished2}</div>}
          {recent.map((b) => <Item key={b.id} b={b} />)}
          {older.length > 0 && !all && <button type="button" className="bp-more" onClick={() => setAll(true)}>{txt.showMore(older.length)}</button>}
          {all && [...byYear.entries()].map(([year, list]) => (
            <div key={year}><div className="bp-head">{year}</div>{list.map((b) => <Item key={b.id} b={b} />)}</div>
          ))}
        </div>
      )}
    </div>
  );
}
